import { CSmartTable } from '@coreui/react-pro'
import { Button, CircularProgress, ToggleButton, ToggleButtonGroup, Tooltip } from '@mui/material'
import PersonAddIcon from '@mui/icons-material/PersonAdd'
import { Fragment, useEffect, useMemo, useState } from 'react'
import dayjs from 'dayjs'
import capitalize from 'lodash/capitalize'
import tokenBackgroundIcon from 'src/assets/svg/hp-token-icon.svg'
import ConsultsActions from 'src/clinic/components/ClinicManageBookingsPage/consultsTab/components/ConsultsActions'
import ConsultsCard from 'src/clinic/components/ClinicManageBookingsPage/consultsTab/components/ConsultsCard'
import ConsultsDetailsCell from 'src/clinic/components/ClinicManageBookingsPage/consultsTab/components/ConsultsDetailsCell'
import ConsultsVitalsCell from 'src/clinic/components/ClinicManageBookingsPage/consultsTab/components/ConsultsVitalsCell'
import { useScreenSize } from 'src/customHooks/customHooks'
import { getBookedSlotStripColor } from 'src/clinic/components/ClinicManageBookingsPage/DayViewCalendarComponent'
import ContactDetailsTableItem from 'src/clinic/components/ClinicManageBookingsPage/slotItem/component/ContactDetailsTableItem'
import InTherapyButton from 'src/clinic/components/ClinicManageBookingsPage/slotItem/component/InTherapyButton'
import { ConsultsTabTableContainer } from 'src/clinic/components/ClinicManageBookingsPage/slotItem/styledComponent'
import { shouldShowPrescriptionPdfDownloadButtons } from 'src/clinic/components/ClinicManageBookingsPage/utils'
import {
  EVENT_TYPE_CONSULTATION,
  EVENT_TYPE_THERAPY_SESSION,
  MOMENT_FORMATS,
} from 'src/consumer/constants'
import { primeDiagnosesForDay } from 'src/clinic/components/ClinicManageBookingsPage/consultsTab/diagnosis/apptDiagnosisManager'
import { getAgeFromDobWithTime } from 'src/consumer/helpers/profilePageHelper'
import { getMeetingSrcIcon, getMeetingSrcLabel } from 'src/consumer/helpers/meetingSrcUtils'
import { getCSmartTableColumnData, getExactTimeDiff } from 'src/consumer/helpers/utilFunctions'
import { preparePatientLabelFromPatientInfoObject } from 'src/supportPortal/views/scheduledPatients/utils'

export default function ConsultsTab({
  bookedEventsListWithPatientInfo,
  loadingTableData,
  viewButtonClick,
  showActionButtons = false,
  mentorProfileData,
  clinicLocationData,
  handleNewTherapyRequest,
  setClickedBookedSlotData,
  therapyBookingDataForThisEvent,
  setEditPatientDialogOpen,
  forMentor,
  setAddVitalsDialogOpen,
  setEditBookingDialogOpen,
  refreshBookedSlots,
  sendPrescriptionPdfApiSignature,
  prescriptionGetApiSignature,
  prescriptionSaveApiSignature,
  fileUploadApiSignature,
  showGalleryForPrescriptionImages,
  sendSecureSessionLinkToPatientSignature,
  sendPublicSessionLinkToPatientSignature,
  prescriptionImagesGetApiSignature,
  prescriptionImagesSaveApiSignature,
  prescriptionViewSettings,
  hidePatientContactDetails = false,
  // Opens the "Search or Create Patient" booking dialog. When provided (mentor flow), an inline
  // "New appointment" button is shown in the mobile list just before the Completed divider.
  onNewBooking,
  // When the host lifts sorting into the main header (mentor flow) it passes the current sort +
  // an onSortChange; the in-list toggle is then hidden and this becomes controlled.
  sortMode: controlledSortMode,
  onSortChange,
}) {
  // 'default' = completed sessions grouped to the bottom; 'startTime' = pure start-time ascending.
  const [localSortMode, setLocalSortMode] = useState('default')
  const isSortControlled = typeof onSortChange === 'function'
  const sortMode = isSortControlled ? controlledSortMode || 'default' : localSortMode

  // Below this width the dense table is unusable (horizontal scroll), so each
  // appointment renders as a stacked, touch-friendly card instead.
  const { width } = useScreenSize()
  const isMobile = width < 768

  // Props shared by every mobile card (the same set scopedColumns receives).
  const cardProps = {
    viewButtonClick,
    showActionButtons,
    hidePatientContactDetails,
    mentorProfileData,
    clinicLocationData,
    handleNewTherapyRequest,
    setClickedBookedSlotData,
    therapyBookingDataForThisEvent,
    setEditPatientDialogOpen,
    forMentor,
    setAddVitalsDialogOpen,
    setEditBookingDialogOpen,
    refreshBookedSlots,
    prescriptionViewSettings,
    sendPrescriptionPdfApiSignature,
    prescriptionGetApiSignature,
    prescriptionSaveApiSignature,
    fileUploadApiSignature,
    showGalleryForPrescriptionImages,
    sendSecureSessionLinkToPatientSignature,
    sendPublicSessionLinkToPatientSignature,
    prescriptionImagesGetApiSignature,
    prescriptionImagesSaveApiSignature,
  }

  // Prime the whole day's appointment labels into the cache in ONE bulk call (mentor flow only) so
  // the per-row chips render from cache with no per-row network. Consultations resolve to a doctor
  // Diagnosis/Complaint label; therapy sessions resolve to the therapy type. Deduped + idle-safe.
  useEffect(() => {
    if (!forMentor || !Array.isArray(bookedEventsListWithPatientInfo)) return
    const candidates = bookedEventsListWithPatientInfo
      .filter(
        (e) =>
          e?.id &&
          (e?.eventType === EVENT_TYPE_CONSULTATION || e?.eventType === EVENT_TYPE_THERAPY_SESSION),
      )
      .map((e) => ({
        eventId: e.id,
        eventType: e.eventType,
        dayKey: e?.startTime ? dayjs(e.startTime).format('YYYY-MM-DD') : '',
      }))
    if (candidates.length > 0) {
      primeDiagnosesForDay(candidates)
    }
  }, [forMentor, bookedEventsListWithPatientInfo])

  // Build the row list, applying the chosen sort and tagging completed rows so
  // they render in green below a bold divider line (in the grouped/default mode).
  const finalBInfoTableItems = useMemo(() => {
    const rows = [...(bookedEventsListWithPatientInfo || [])]

    if (sortMode === 'startTime') {
      rows.sort((a, b) => {
        const aTime = a?.startTime ? dayjs.utc(a.startTime).valueOf() : Number.POSITIVE_INFINITY
        const bTime = b?.startTime ? dayjs.utc(b.startTime).valueOf() : Number.POSITIVE_INFINITY
        return aTime - bTime
      })
    } else {
      rows.sort((a, b) =>
        a?.markedCompleted === b?.markedCompleted ? 0 : a?.markedCompleted ? 1 : -1,
      )
    }

    return rows.map((event, index) => {
      const isCompleted = Boolean(event?.markedCompleted)
      // The bold divider sits on the first completed row that follows a
      // non-completed one — only meaningful when completed rows are grouped.
      const isFirstCompleted =
        isCompleted && sortMode === 'default' && index > 0 && !rows[index - 1]?.markedCompleted

      const className = [
        isCompleted ? 'consults-completed-row' : '',
        isFirstCompleted ? 'consults-completed-divider' : '',
      ]
        .filter(Boolean)
        .join(' ')

      return {
        ...event,
        _props: {
          ...(className ? { className } : {}),
          style: {
            verticalAlign: 'middle',
            background: getBookedSlotStripColor(event).consultsTableColor,
          },
        },
      }
    })
  }, [bookedEventsListWithPatientInfo, sortMode])

  // Where the completed section begins (the divider row), or -1 if there is none.
  const completedDividerIndex = finalBInfoTableItems.findIndex((it) =>
    it?._props?.className?.includes('consults-completed-divider'),
  )

  const newBookingButton =
    forMentor && onNewBooking ? (
      <Button
        onClick={onNewBooking}
        startIcon={<PersonAddIcon sx={{ fontSize: 18 }} />}
        sx={{
          textTransform: 'none',
          fontWeight: 700,
          fontSize: '13px',
          color: '#0f9d6b',
          border: '1.5px dashed rgba(16, 185, 129, 0.5)',
          borderRadius: '12px',
          padding: '10px',
          backgroundColor: 'rgba(16, 185, 129, 0.04)',
          '&:hover': {
            backgroundColor: 'rgba(16, 185, 129, 0.1)',
            borderColor: 'rgba(16, 185, 129, 0.7)',
          },
        }}
      >
        New appointment
      </Button>
    ) : null

  return (
    <ConsultsTabTableContainer>
      {!isSortControlled && (
        <div
          className="d-flex align-items-center justify-content-end gap-2"
          style={{ padding: '8px 4px' }}
        >
          <span style={{ fontSize: '13px', color: '#6b7280', fontWeight: 600 }}>Sort</span>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={sortMode}
            onChange={(event, value) => {
              if (value) {
                setLocalSortMode(value)
              }
            }}
          >
            <ToggleButton value="default" sx={{ textTransform: 'none' }}>
              Default
            </ToggleButton>
            <ToggleButton value="startTime" sx={{ textTransform: 'none' }}>
              By start time
            </ToggleButton>
          </ToggleButtonGroup>
        </div>
      )}
      {isMobile ? (
        <div
          className="d-flex flex-column"
          style={{ gap: '10px', padding: '4px 2px 16px' }}
        >
          {loadingTableData ? (
            <div className="d-flex justify-content-center" style={{ padding: '28px' }}>
              <CircularProgress size={28} />
            </div>
          ) : finalBInfoTableItems.length === 0 ? (
            <>
              <div
                style={{
                  textAlign: 'center',
                  color: '#6b7280',
                  padding: '28px 12px',
                  fontSize: '14px',
                }}
              >
                No appointments for this day.
              </div>
              {newBookingButton}
            </>
          ) : (
            <>
              {finalBInfoTableItems.map((item, index) => {
                const showCompletedDivider = item?._props?.className?.includes(
                  'consults-completed-divider',
                )
                return (
                  <Fragment key={item?.id || item?.uid || index}>
                    {/* The "New appointment" button sits just before the Completed divider — i.e.
                        after the upcoming appointments, ahead of the completed ones. */}
                    {index === completedDividerIndex && newBookingButton}
                    {showCompletedDivider && (
                      <div
                        className="d-flex align-items-center"
                        style={{ gap: '8px', margin: '6px 2px 2px' }}
                      >
                        <span
                          style={{ height: '2px', flex: 1, background: '#059669', borderRadius: '2px' }}
                        />
                        <span
                          style={{
                            fontSize: '11px',
                            fontWeight: 700,
                            color: '#047857',
                            textTransform: 'uppercase',
                            letterSpacing: '0.4px',
                          }}
                        >
                          Completed
                        </span>
                        <span
                          style={{ height: '2px', flex: 1, background: '#059669', borderRadius: '2px' }}
                        />
                      </div>
                    )}
                    <ConsultsCard bookedSlot={item} {...cardProps} />
                  </Fragment>
                )
              })}
              {/* No completed section (nothing to anchor before) → append at the end of the list. */}
              {completedDividerIndex === -1 && newBookingButton}
            </>
          )}
        </div>
      ) : (
        <CSmartTable
          items={finalBInfoTableItems}
          columns={columns({ viewButtonClick, showActionButtons })}
          loading={loadingTableData}
          tableProps={{
            hover: true,
            striped: true,
            responsive: true,
          }}
          itemsPerPage={10000000000000000}
          scopedColumns={scopedColumns({
          viewButtonClick,
          mentorProfileData,
          clinicLocationData,
          handleNewTherapyRequest,
          setClickedBookedSlotData,
          therapyBookingDataForThisEvent,
          setEditPatientDialogOpen,
          forMentor,
          setAddVitalsDialogOpen,
          setEditBookingDialogOpen,
          refreshBookedSlots,
          prescriptionViewSettings,
          sendPrescriptionPdfApiSignature,
          prescriptionGetApiSignature,
          prescriptionSaveApiSignature,
          fileUploadApiSignature,
          showGalleryForPrescriptionImages,
          sendSecureSessionLinkToPatientSignature,
          sendPublicSessionLinkToPatientSignature,
          prescriptionImagesGetApiSignature,
          prescriptionImagesSaveApiSignature,
          hidePatientContactDetails,
          })}
        />
      )}
    </ConsultsTabTableContainer>
  )
}

function columns({ hideClinicDetails = false, viewButtonClick, showActionButtons }) {
  // Columns are clubbed by meaning: the appointment (token + mode), the patient
  // (name, age/gender, ID, visit no., contact), the session (type + screening +
  // status), timing (booking + wait), vitals, and the row actions.
  const cols = [
    getCSmartTableColumnData({
      key: 'appointment',
      label: 'Appointment',
      filter: false,
      sorter: false,
      _style: { width: '150px', minWidth: '150px' },
    }),
    getCSmartTableColumnData({
      key: 'patient',
      label: 'Patient',
      filter: false,
      sorter: false,
      _style: { minWidth: '190px' },
    }),
    getCSmartTableColumnData({
      key: 'purpose',
      label: 'Type & Status',
      filter: false,
      sorter: false,
      _style: { minWidth: '200px' },
    }),
    getCSmartTableColumnData({
      key: 'timing',
      label: 'Timing',
      filter: false,
      sorter: false,
      _style: { maxWidth: '150px' },
    }),
    getCSmartTableColumnData({
      key: 'vitals',
      label: 'Vitals',
      filter: false,
      sorter: false,
      _style: { maxWidth: '140px' },
    }),
    getCSmartTableColumnData({
      key: 'view',
      label: viewButtonClick ? 'View' : 'Session Info',
      filter: false,
      sorter: false,
      _style: { maxWidth: '150px' },
    }),
  ]

  if (showActionButtons) {
    cols.push(
      getCSmartTableColumnData({
        key: 'actions',
        label: 'Actions',
        filter: false,
        sorter: false,
        _style: { maxWidth: '90px' },
      }),
    )
  }
  return cols
}

function scopedColumns({
  viewButtonClick,
  mentorProfileData,
  clinicLocationData,
  handleNewTherapyRequest,
  setClickedBookedSlotData,
  therapyBookingDataForThisEvent,
  setEditPatientDialogOpen,
  forMentor,
  setAddVitalsDialogOpen,
  setEditBookingDialogOpen,
  refreshBookedSlots,
  prescriptionViewSettings,
  sendPrescriptionPdfApiSignature,
  prescriptionGetApiSignature,
  prescriptionSaveApiSignature,
  fileUploadApiSignature,
  showGalleryForPrescriptionImages,
  sendSecureSessionLinkToPatientSignature,
  sendPublicSessionLinkToPatientSignature,
  prescriptionImagesGetApiSignature,
  prescriptionImagesSaveApiSignature,
  hidePatientContactDetails = false,
}) {
  return {
    // TODO: add online of offline session info here
    // Appointment = token number + how the patient is coming in (in-person / video / home).
    appointment: (bookedEventInfo) => {
      const meetingSrc = bookedEventInfo?.meetingSrc
      return (
        <td>
          <div
            className="d-flex flex-column align-items-center gap-1"
            style={{ minWidth: '120px' }}
          >
            <div
              style={{
                position: 'relative',
                width: '100%',
                minHeight: '40px',
                backgroundImage: `url(${tokenBackgroundIcon})`,
                backgroundRepeat: 'no-repeat',
                backgroundSize: 'contain',
                backgroundPosition: 'center',
              }}
              className="d-flex align-items-center justify-content-center"
            >
              <span style={{ color: '#8f8f9f', zIndex: 1, position: 'relative', fontWeight: 600 }}>
                {bookedEventInfo?.appointmentNumber}
              </span>
            </div>
            <Tooltip title={getMeetingSrcLabel(meetingSrc)} arrow>
              <div
                className="d-flex align-items-center gap-1"
                style={{ color: '#475569', fontSize: '12px', whiteSpace: 'nowrap' }}
              >
                {getMeetingSrcIcon(meetingSrc, { size: 16, color: '#6366F1' })}
                <span>{getMeetingSrcLabel(meetingSrc)}</span>
              </div>
            </Tooltip>
          </div>
        </td>
      )
    },
    // Patient = name, age/gender, visit no., patient ID and contact details, all together.
    patient: (bookedEventInfo) => {
      const patientInfo = bookedEventInfo?.patientInfo
      const visitNo = bookedEventInfo?.patientMeetingSeqNumber
      return (
        <td>
          <div className="d-flex flex-column gap-1">
            <div className="d-flex align-items-center gap-1">
              {bookedEventInfo?.therapyPatient && (
                <InTherapyButton bookedSlot={bookedEventInfo} slotBlocked={false} />
              )}
              <span style={{ fontWeight: 600 }}>
                {preparePatientLabelFromPatientInfoObject(patientInfo, true)}
              </span>
            </div>
            <div style={{ fontSize: '12px', color: '#6b7280' }}>
              {getAgeFromDobWithTime(patientInfo?.dateOfBirth)} Y / {capitalize(patientInfo?.gender)}
              {visitNo ? ` · Visit #${visitNo}` : ''}
            </div>
            <div style={{ fontSize: '12px', color: '#8f8f9f' }}>
              {patientInfo?.patientNumber
                ? `ID: ${patientInfo.patientNumber}`
                : 'Patient no. not assigned'}
            </div>
            <ContactDetailsTableItem
              bookedEventInfo={bookedEventInfo}
              isTableItem={false}
              hidePatientContactDetails={hidePatientContactDetails}
            />
          </div>
        </td>
      )
    },
    // Timing = scheduled booking time + how long the patient waited.
    timing: (bookedEventInfo) => {
      const slotStart = bookedEventInfo?.startTime
        ? dayjs.utc(bookedEventInfo.startTime).local()
        : null
      const sessionStart = bookedEventInfo?.sessionStartedAt
        ? dayjs.utc(bookedEventInfo.sessionStartedAt).local()
        : null

      let waitContent = '--'
      if (sessionStart && slotStart) {
        // Session started — show difference between sessionStart and slotStart
        waitContent = getExactTimeDiff(slotStart, sessionStart, false, false, false)
      } else if (slotStart && slotStart.isSame(dayjs(), 'day')) {
        // No session start, but slotStart is today — show wait time since slotStart
        waitContent = getExactTimeDiff(slotStart, dayjs(), false, false, false)
      }

      return (
        <td>
          <div className="d-flex flex-column gap-1">
            <div style={{ fontWeight: 600 }}>
              {slotStart ? slotStart.format(MOMENT_FORMATS.formattedTime24Hrs) : 'No Data'}
            </div>
            <div style={{ fontSize: '12px', color: '#6b7280' }}>Wait: {waitContent}</div>
          </div>
        </td>
      )
    },
    view: (bookedEventInfo) => {
      // If viewButtonClick is not provided (e.g., for Clinic Assistants), show meaningful information instead
      if (!viewButtonClick) {
        const sessionStatus = bookedEventInfo?.markedCompleted
          ? 'Completed'
          : bookedEventInfo?.sessionStartedAt
          ? 'In Progress'
          : 'Upcoming'
        
        const platformType = bookedEventInfo?.meetingSrc === 'aumhum' ? 'Online' : 'Offline'
        const eventType = bookedEventInfo?.title || bookedEventInfo?.eventType || 'Session'
        
        return (
          <td>
            <div className="d-flex flex-column gap-1" style={{ fontSize: '12px' }}>
              <div style={{ fontWeight: 600, color: '#333' }}>
                {sessionStatus}
              </div>
              <div style={{ color: '#666' }}>
                {platformType} • {eventType}
              </div>
              {bookedEventInfo?.sessionStartedAt && (
                <div style={{ color: '#888', fontSize: '11px' }}>
                  Started: {dayjs.utc(bookedEventInfo.sessionStartedAt).local().format('HH:mm')}
                </div>
              )}
            </div>
          </td>
        )
      }
      
      // For mentors, show the View button
      return (
        <td>
          <Button
            variant="contained"
            onClick={() => {
              viewButtonClick(bookedEventInfo)
            }}
          >
            View
          </Button>
        </td>
      )
    },
    purpose: (bookedEventInfo) => {
      return (
        <td>
          <ConsultsDetailsCell bookedSlot={bookedEventInfo} />
        </td>
      )
    },
    vitals: (bookedEventInfo) => {
      return (
        <td>
          <ConsultsVitalsCell
            bookedSlot={bookedEventInfo}
            onOpenVitals={
              setAddVitalsDialogOpen
                ? (slot) => {
                    setClickedBookedSlotData?.(slot)
                    setAddVitalsDialogOpen(true)
                  }
                : undefined
            }
          />
        </td>
      )
    },
    actions: (bookedEventInfo) => {
      return (
        <td>
          <ConsultsActions
            bookedSlot={bookedEventInfo}
            mentorProfileData={mentorProfileData}
            clinicLocationData={clinicLocationData}
            handleNewTherapyRequest={handleNewTherapyRequest}
            setClickedBookedSlotData={setClickedBookedSlotData}
            therapyBookingDataForThisEvent={therapyBookingDataForThisEvent}
            setEditPatientDialogOpen={setEditPatientDialogOpen}
            forMentor={forMentor}
            setAddVitalsDialogOpen={setAddVitalsDialogOpen}
            setEditBookingDialogOpen={setEditBookingDialogOpen}
            refreshBookedSlots={refreshBookedSlots}
            showPrescriptionPdfDownloadButtons={shouldShowPrescriptionPdfDownloadButtons({
              forMentor,
              bookedSlot: bookedEventInfo,
              prescriptionViewSettingsData: prescriptionViewSettings,
            })}
            sendPrescriptionPdfApiSignature={sendPrescriptionPdfApiSignature}
            prescriptionGetApiSignature={prescriptionGetApiSignature}
            prescriptionSaveApiSignature={prescriptionSaveApiSignature}
            fileUploadApiSignature={fileUploadApiSignature}
            showGalleryForPrescriptionImages={showGalleryForPrescriptionImages}
            sendSecureSessionLinkToPatientSignature={sendSecureSessionLinkToPatientSignature}
            sendPublicSessionLinkToPatientSignature={sendPublicSessionLinkToPatientSignature}
            prescriptionImagesGetApiSignature={prescriptionImagesGetApiSignature}
            prescriptionImagesSaveApiSignature={prescriptionImagesSaveApiSignature}
          />
        </td>
      )
    },
  }
}
